import { GA4_ID, PIXEL_ID, TIKTOK_PIXEL_ID } from './config.js';

let initialized = false;

export function initAnalytics() {
  if (initialized) return;
  if (!import.meta.env.PROD) return;
  initialized = true;

  // ── Google Analytics 4 ──────────────────────────────────────────────
  if (GA4_ID) {
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA4_ID, { send_page_view: true });
  }

  // ── Meta Pixel ──────────────────────────────────────────────────────
  if (PIXEL_ID) {
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v;
      s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    window.fbq('init', PIXEL_ID);
    window.fbq('track', 'PageView');
  }

  // ── TikTok Pixel ────────────────────────────────────────────────────
  if (TIKTOK_PIXEL_ID) {
    !function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = w[t] = w[t] || [];
      ttq.methods = ['page','track','identify','instances','debug','on','off','once','ready','alias','group','enableCookie','disableCookie'];
      ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; };
      for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.instance = function (t) { for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++) ttq.setAndDefer(e, ttq.methods[n]); return e; };
      ttq.load = function (e, n) {
        var i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
        ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = i;
        ttq._t = ttq._t || {}; ttq._t[e] = +new Date;
        ttq._o = ttq._o || {}; ttq._o[e] = n || {};
        var o = d.createElement('script'); o.type = 'text/javascript'; o.async = !0;
        o.src = i + '?sdkid=' + e + '&lib=' + t;
        var a = d.getElementsByTagName('script')[0]; a.parentNode.insertBefore(o, a);
      };
      ttq.load(TIKTOK_PIXEL_ID);
      ttq.page();
    }(window, document, 'ttq');
  }
}

// A WhatsApp click is an INTENT signal, not a lead.
//
// This used to fire fbq('track','Lead') here, which meant Meta optimized the
// campaigns for "people who click the WhatsApp button" — a metric that is
// trivially easy to maximize with low-quality traffic. The real lead event is
// now trackLead(), fired only when a lead actually lands in the CRM.
//
// ⚠ ACTION REQUIRED IN META ADS MANAGER after this deploys: any campaign
// currently optimizing for the `Lead` event was optimizing on clicks. Repoint
// it at the new server-side `Lead` event (or at `Contact` if you deliberately
// want click optimization). Until you do, those campaigns lose their
// optimization signal, because clicks no longer report as Lead.
export function trackWhatsApp(source) {
  if (typeof window === 'undefined') return;
  if (window.gtag) window.gtag('event', 'whatsapp_click', { source });
  if (window.fbq) window.fbq('track', 'Contact', { content_name: source });
  if (window.ttq) window.ttq.track('ClickButton', { content_name: source });
}

/**
 * The real conversion: a lead that reached our own backend.
 *
 * `eventId` must be the same string the server sends to the Meta Conversions
 * API for this submission (api/lib/meta-capi.js). Meta deduplicates browser
 * and server events that share event_id + event_name, so the conversion is
 * counted once whether or not the browser pixel was blocked. Without the
 * shared id you get double-counted conversions and inflated ROAS.
 */
export function trackLead({ eventId, service = null, value = null } = {}) {
  if (typeof window === 'undefined') return;
  if (window.gtag) {
    window.gtag('event', 'generate_lead', {
      transaction_id: eventId,
      currency: 'ILS',
      value: value ?? undefined,
      items: service ? [{ item_name: service }] : undefined,
    });
  }
  if (window.fbq) {
    window.fbq(
      'track',
      'Lead',
      { content_name: service ?? 'lead-form', currency: 'ILS', value: value ?? undefined },
      eventId ? { eventID: eventId } : undefined,
    );
  }
  if (window.ttq) {
    window.ttq.track('SubmitForm', { content_name: service ?? 'lead-form' }, eventId ? { event_id: eventId } : undefined);
  }
}

export function trackPageView(path) {
  if (typeof window === 'undefined') return;
  if (window.gtag && GA4_ID) window.gtag('config', GA4_ID, { page_path: path });
  if (window.fbq) window.fbq('track', 'PageView');
  if (window.ttq) window.ttq.page();
}
